import type {
  ColumnDef,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  type ComponentType,
  type ForwardedRef,
  forwardRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/lib/cn';

import { TableBody } from './components/TableBody';
import { TableCell } from './components/TableCell';
import { TableCellDefaultSkeleton } from './components/TableCellDefaultSkeleton';
import {
  TableElement,
  type TableElementProps,
} from './components/TableElement/TableElement';
import { TableHead } from './components/TableHead';
import { TableHeadCell } from './components/TableHeadCell';
import { TableRow } from './components/TableRow';

export interface TableProps<TData extends { id: string }>
  extends Omit<TableElementProps<TData>, 'data' | 'table'> {
  CellSkeleton?: ComponentType<unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  data: TData[];
  isLoading?: boolean;
  loadingRowCount?: number;

  // rely on server-side sorting
  // if true, the table will not handle sorting on the client side
  manualSorting?: boolean;

  // Row specific props
  onRowClick?: (row: TData) => void;

  // Sorting specific props
  onSortingChange?: OnChangeFn<SortingState>;
  sort?: SortingState;
}

// eslint-disable-next-line react/display-name
function TableInner<TData extends { id: string }>(
  {
    CellSkeleton = TableCellDefaultSkeleton,
    columns,
    data,
    isLoading = false,
    loadingRowCount = 10,
    manualSorting = true,
    onRowClick,
    onSortingChange,
    sort = [],
    ...tableProps
  }: TableProps<TData>,
  ref: ForwardedRef<HTMLTableElement>,
) {
  const { t } = useTranslation('chords', { keyPrefix: 'table' });

  const tableData = useMemo(
    () => (isLoading ? Array(loadingRowCount).fill({}) : data),
    [isLoading, loadingRowCount, data],
  );

  const columnsMemo = useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            cell: CellSkeleton,
          }))
        : columns,
    [isLoading, columns, CellSkeleton],
  ) as typeof columns;

  const table = useReactTable({
    columns: columnsMemo,
    data: tableData,
    // Enable sorting only if onSortingChange is provided
    enableSorting: !!onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
    onSortingChange,
    // This prevents invalid handling of sort direction for columns with incomplete data
    // See more: https://github.com/TanStack/table/issues/4289
    sortDescFirst: true,
    state: {
      sorting: sort,
    },
  });

  return (
    <div className={cn('w-full overflow-x-auto')}>
      <TableElement<TData> {...tableProps} table={table} ref={ref}>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeadCell key={header.id} header={header} />
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {data.length || isLoading ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={
                  !isLoading && onRowClick
                    ? () => onRowClick?.(row.original)
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    cell={cell}
                    isInteractive={!!onRowClick}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              {/* TODO: replace this with DataUnavailable or similar */}
              <TableCell colSpan={columns.length}>{t('noData')}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableElement>
    </div>
  );
}

const TableBase = forwardRef(TableInner);

TableBase.displayName = 'Table';

type TableComponent = <TData extends { id: string }>(
  props: TableProps<TData> & {
    ref?: ForwardedRef<HTMLTableElement>;
  },
) => ReturnType<typeof TableInner>;

export const Table = TableBase as unknown as TableComponent & {
  Body: typeof TableBody;
  Cell: typeof TableCell;
  Head: typeof TableHead;
  HeadCell: typeof TableHeadCell;
  Row: typeof TableRow;
  Table: typeof TableElement;
};

Table.Body = TableBody;
Table.Cell = TableCell;
Table.Head = TableHead;
Table.HeadCell = TableHeadCell;
Table.Row = TableRow;
Table.Table = TableElement;
